import React from "react";
import { motion } from "framer-motion";
import { ArrowUpRight, ArrowRight, Home, Layers, Bath, Hammer, Building2 } from "lucide-react";
import { MAJOR_PROJECTS } from "../../content/hubs";
import { IMAGES, track } from "../../data";

const reveal = {
  hidden: { opacity: 0, y: 26 },
  show: (i) => ({ opacity: 1, y: 0, transition: { duration: 0.55, ease: [0.22, 1, 0.36, 1], delay: (i % 3) * 0.06 } }),
};

// Kept for the "different project" link: preselects a type in the homepage form and scrolls to it.
export const preselectAndScroll = (type) => {
  track("service_card_click", { project_type: type });
  window.dispatchEvent(new CustomEvent("hpc:preselect", { detail: type }));
  const el = document.getElementById("request");
  if (el) el.scrollIntoView({ behavior: "smooth" });
};

const CARD_META = {
  "demolition": { image: IMAGES.exterior, alt: "Residential selective demolition and structural work", icon: Hammer, span: "lg:col-span-5", minH: "min-h-[320px]", desc: "Selective demolition, wall removal, and clean prep for what comes next." },
  "home-additions": { image: IMAGES.addition, alt: "Modern home addition exterior", icon: Home, span: "lg:col-span-7", minH: "min-h-[320px]", desc: "New rooms, second stories, primary suites, and expanded living space." },
  "kitchen-and-bath-remodels": { image: IMAGES.kitchen, alt: "Kitchen and bathroom renovation", icon: Bath, span: "lg:col-span-7", minH: "min-h-[320px]", desc: "New layouts, cabinetry, fixtures, tile, and finishes for the hardest-working rooms." },
  "complete-home-remodels": { image: IMAGES.remodel, alt: "Complete home remodel interior", icon: Layers, span: "lg:col-span-5", minH: "min-h-[320px]", desc: "Whole-home transformations that reimagine layout, systems, and finishes." },
  "home-building": { image: IMAGES.blueprint, alt: "Custom home construction blueprints", icon: Building2, span: "md:col-span-2 lg:col-span-12", minH: "min-h-[240px]", desc: "From lot evaluation to move-in day — a general contractor coordinates every trade." },
};

const Card = ({ p, i }) => {
  const meta = CARD_META[p.slug];
  const Icon = meta.icon;
  return (
    <motion.a
      href={`/sc/major-projects/${p.slug}/`}
      data-testid={`service-card-${p.slug}`}
      aria-label={`${p.name} — learn more`}
      onClick={() => track("major_project_card_click", { slug: p.slug })}
      custom={i}
      variants={reveal}
      initial="hidden"
      whileInView="show"
      viewport={{ once: true, amount: 0.2 }}
      className={`group relative overflow-hidden rounded-[1.5rem] border border-[rgba(19,57,85,0.08)] bg-white shadow-[0_8px_32px_rgba(19,57,85,0.06)] text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2 block ${meta.span} ${meta.minH}`}
    >
      <div className="absolute inset-0">
        <img
          src={meta.image}
          alt={meta.alt}
          loading="lazy"
          width="800"
          height="600"
          className="h-full w-full object-cover transition-transform duration-[900ms] ease-out group-hover:scale-105"
        />
        <div className="absolute inset-0 bg-gradient-to-t from-[#133955] via-[#133955]/55 to-[#133955]/10" />
      </div>

      <div className="relative z-10 h-full flex flex-col justify-between p-7 md:p-8">
        <span className="grid place-items-center w-12 h-12 rounded-full bg-white/15 backdrop-blur-md text-white border border-white/20">
          <Icon size={22} />
        </span>
        <div>
          <div className="flex items-center justify-between gap-3">
            <h3 className="font-display text-white font-bold tracking-tight text-2xl md:text-[1.7rem] leading-tight">
              {p.name}
            </h3>
            <span className="grid place-items-center w-9 h-9 rounded-full bg-[#54BCE6] text-[#133955] shrink-0 translate-y-1 opacity-0 group-hover:opacity-100 group-hover:translate-y-0 transition-all duration-300">
              <ArrowUpRight size={18} />
            </span>
          </div>
          <p className="mt-2 text-sm text-white/80 max-w-md">{meta.desc}</p>
        </div>
      </div>
    </motion.a>
  );
};

export const Services = () => {
  return (
    <section id="services" data-testid="services-section" className="bg-white py-16 md:py-24 scroll-mt-24">
      <div className="max-w-7xl mx-auto px-6 md:px-12">
        <div className="max-w-3xl mb-10 md:mb-14">
          <motion.p
            initial={{ opacity: 0, y: 14 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.5 }}
            className="text-xs uppercase tracking-[0.24em] font-bold text-[#54BCE6] mb-4"
          >
            Major projects
          </motion.p>
          <motion.h2
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6 }}
            className="font-display text-[#133955] font-extrabold tracking-tighter text-4xl md:text-5xl lg:text-6xl leading-[0.98]"
          >
            Big Builds Start With the Right General Contractor.
          </motion.h2>
          <motion.p
            initial={{ opacity: 0, y: 18 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, amount: 0.4 }}
            transition={{ duration: 0.6, delay: 0.08 }}
            className="mt-5 text-base md:text-lg text-[#173042]/75 max-w-xl"
          >
            One contractor coordinates every trade, permit, and inspection. Explore the project
            types we help connect — or tell us what you're planning.
          </motion.p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-12 gap-6 md:gap-7 auto-rows-fr">
          {MAJOR_PROJECTS.map((p, i) => (
            <Card key={p.slug} p={p} i={i} />
          ))}
        </div>

        <div className="mt-10 text-center">
          <button
            type="button"
            data-testid="services-other-link"
            onClick={() => preselectAndScroll("Other Major Home Improvement")}
            className="group inline-flex items-center gap-2 text-base font-bold text-[#133955] hover:text-[#54BCE6] transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[#54BCE6] focus-visible:ring-offset-2 rounded-sm"
          >
            Have a different project? Tell us about it.
            <ArrowRight size={18} className="transition-transform duration-300 group-hover:translate-x-1" />
          </button>
        </div>
      </div>
    </section>
  );
};
